import { createApiRef } from '@backstage/core-plugin-api';
import type {
  ChatMessage,
  ChatArtifact,
  SreTask,
  SreExpert,
  FunctionType,
  SecurityAlert,
} from '../types';

/**
 * SA_SRE Chat API Interface
 *
 * Defines the contract for communicating with the superagent-sre backend
 * through the Backstage proxy layer.
 */
export interface SaSreChatApi {
  /**
   * Set the current active function type.
   * Used by the client for mock fallback when backend is unavailable.
   */
  setCurrentFunction(fn: FunctionType): void;

  /**
   * Send a chat message and receive streaming SSE response.
   * @param message - User's message text
   * @param activeTask - Currently selected SRE task ID
   * @param experts - List of enabled expert IDs
   * @param onChunk - Callback for each SSE chunk (message or artifact)
   * @param file - Optional file attachment
   * @returns Promise that resolves when the stream completes
   */
  sendMessage(
    message: string,
    activeTask: string | null,
    experts: string[],
    onChunk: (chunk: {
      type: 'message' | 'artifact';
      message?: ChatMessage;
      artifact?: ChatArtifact;
    }) => void,
    file?: File,
  ): Promise<void>;

  /**
   * Generate context-aware suggestions for the chat input.
   */
  getSuggestions(
    activeTask: string | null,
    artifactContent?: string,
  ): Promise<string[]>;

  /**
   * Fetch tasks for a given SRE function type.
   */
  getTasks(functionType: FunctionType): Promise<SreTask[]>;

  /**
   * Fetch expert roster for a given SRE function type.
   */
  getExperts(functionType: FunctionType): Promise<SreExpert[]>;

  /**
   * Fetch unified SRE dashboard data.
   */
  getDashboard(): Promise<Record<string, unknown>>;

  /**
   * Fetch security alerts with optional filters.
   */
  getAlerts(params?: {
    provider?: string;
    severity?: string;
    status?: string;
    limit?: number;
  }): Promise<SecurityAlert[]>;

  /**
   * Trigger agent analysis for a dashboard action.
   */
  triggerAnalysis(action: {
    id: string;
    title: string;
    domain: string;
    type: string;
    context?: Record<string, unknown>;
  }): Promise<{ report: string; generatedAt: string }>;
}

/**
 * API Reference for dependency injection via Backstage's useApi() hook.
 */
export const saSreChatApiRef = createApiRef<SaSreChatApi>({
  id: 'plugin.sa-sre-chat.api',
});
